import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useRoleStore } from "../../store";
import { useTranslation } from "react-i18next";
import { GetRoleService, RemoveRoleService } from "services/service.cruds";
import { RoleListI } from "interfaces";
import { ActionsTable } from "Common/Components/UI/organisms";
import { NotificationType, showNotifications } from "helpers/toas_notification";
import { useSessionStore } from 'Common/Stores';

export const useListRoles = () => {
  const { session } = useSessionStore();
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [OptionData, setOptionData] = useState<number>(0);
  const [roleRows, setRoleRows] = useState<any[]>([]);
  const deleteToggle = () => setDeleteModal(!deleteModal);
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const {
    setIsEdit,
    setRole,
    handleOpenModalRole,
  } = useRoleStore();

  const columns = [
    {
      header: t("rolesScreen.table.id"),
      accessorKey: 'ID',
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: t("rolesScreen.table.description"),
      accessorKey: "description",
      enableColumnFilter: false,
    },
    {
      header: t("rolesScreen.table.actions"),
      accessorKey: "actions",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ getValue }: { getValue: () => any }) => getValue(),
    },
  ]

  const {
    data: roleData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["security-service-roles"],
    queryFn: () => {
      return GetRoleService();
    },
  });

  useEffect(() => {
    if (roleData) {
      setRoleRows(createCorrelativeRows(roleData));
    }
  }, [roleData]);

  const createCorrelativeRows = (roles: RoleListI[]) => {
    return roles.map((role, index) => ({
      ID: index + 1,
      description: role.description,
      actions: (
        <ActionsTable
          onClickDelete={() => onClickDelete(role.id)}
          onClickEdith={() => handleEditModal(role)}
        ></ActionsTable>
      ),
    }));
  };

  const handleEditModal = (roles: RoleListI) => {
    setRole(roles);
    setIsEdit(true);
    handleOpenModalRole();
  };

  const onClickDelete = (id: number) => {
    setDeleteModal(true);
    if (id) {
      setOptionData(id);
    }
  };

  const onDeleteConfirm = async () => {
    if (isDeleting) return;
    setIsDeleting(true);

    try {
      const responseDelete = await RemoveRoleService(OptionData);

      if (responseDelete.code === '200') {
        queryClient.invalidateQueries({
          queryKey: ['security-service-roles']
        });
        setDeleteModal(false);
        showNotifications(
          NotificationType.SUCCESS,
          t('deleteModal.succedDelete')
        );
      } else {
        showNotifications(
          NotificationType.ERROR,
          t('deleteModal.errorDelete' + responseDelete.message)
        );
      }
    } catch (error) {
      showNotifications(NotificationType.ERROR, t('deleteModal.errorDelete'));
    } finally {
      setDeleteModal(false);
      setIsDeleting(false);
    }
  };

  return {
    columns,
    onDeleteConfirm,
    roleRows,
    deleteModal,
    deleteToggle,
    session,
    isLoading,
    error
  };
};
